import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { lookupDns, DNS_RECORD_TYPES } from '../services/dnsService';
import { getUserIpInfo } from '../services/ipService';
import { isValidDomain, isValidIP } from '../utils/helpers';
import { useDeviceType } from '../hooks/useDeviceType';
import DnsForm from '../components/dns/DnsForm';
import RecordTable from '../components/dns/RecordTable';

const DnsLookupPage = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [searchParams] = useSearchParams();
  const [domain, setDomain] = useState('');
  const [dnsData, setDnsData] = useState(null);
  const [rawDnsData, setRawDnsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userIp, setUserIp] = useState('');

  const handleDnsLookup = async (domain, recordType, queryMethod, server, ecsIp = '') => {
    // 前置校验
    if (!domain || !isValidDomain(domain)) {
      setError({
        type: 'VALIDATION',
        message: t('dns.errors.invalidDomain')
      });
      return;
    }
    if (ecsIp && !isValidIP(ecsIp)) {
      setError({
        type: 'VALIDATION',
        message: t('dns.errors.invalidEcsIp')
      });
      return;
    }

    setLoading(true);
    setError(null);
    setDnsData(null);

    const result = await lookupDns(domain, recordType, queryMethod, server, ecsIp);

    if (result.success) {
      setDnsData(result.data);
      setRawDnsData(result.raw);  // 保存原始数据
    } else {
      // 处理特定错误类型
      if (result.errorCode === 'NXDOMAIN') {
        setError({
          type: 'NO_RECORDS',
          domain: result.domain
        });
      } else if (result.errorCode === 'TIMEOUT') {
        setError({
          type: 'TIMEOUT',
          message: t('dns.errors.timeout')
        });
      } else if (result.errorCode === 'NETWORK') {
        setError({
          type: 'NETWORK',
          message: t('dns.errors.network')
        });
      } else if (result.errorCode === 'RATE_LIMIT') {
        setError({
          type: 'RATE_LIMIT',
          message: t('dns.errors.rateLimit')
        });
      } else {
        setError({
          type: 'GENERAL',
          message: result.errorMessage || t('dns.errors.general')
        });
      }
      setRawDnsData(null);  // 清除原始数据
    }

    setLoading(false);
  };

  useEffect(() => {
    const domainFromQuery = searchParams.get('domain');
    if (domainFromQuery) {
      setDomain(domainFromQuery);
      // 默认使用 DOH (DNS over HTTPS) 查询方式和阿里云服务器
      handleDnsLookup(domainFromQuery, 'A', 'doh', 'alibaba');
    }
  }, [searchParams]);

  // 获取用户当前 IP 地址
  useEffect(() => {
    const fetchUserIp = async () => {
      const result = await getUserIpInfo();
      if (result.success) {
        setUserIp(result.data.ip);
      }
    };

    fetchUserIp();
  }, []);

  const { isMobile, isTablet } = useDeviceType();

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: isMobile ? '1rem' : isTablet ? '1.5rem' : '2rem'
  };

  const titleStyle = {
    textAlign: 'center',
    color: colors.text,
    marginBottom: isMobile ? '1.5rem' : '2rem',
    fontSize: isMobile ? '2rem' : isTablet ? '2.2rem' : '2.5rem',
    fontWeight: '600',
    textShadow: 'none'
  };

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>{t('dns.title')}</h1>
      
      <DnsForm
        domain={domain}
        setDomain={setDomain}
        onLookup={handleDnsLookup}
        loading={loading}
        recordTypes={Object.keys(DNS_RECORD_TYPES)}
        defaultEcsIp={userIp}
      />

      {loading && (
        <div style={{
          textAlign: 'center',
          color: colors.textSecondary,
          fontSize: isMobile ? '1rem' : '1.2rem',
          padding: isMobile ? '1.5rem' : '2rem'
        }}>
          {t('dns.loading')}
        </div>
      )}

      {error && (
        <div style={{
          background: error.type === 'NO_RECORDS' ? colors.warning : colors.error,
          color: 'white',
          padding: '1rem',
          borderRadius: '8px',
          textAlign: 'center',
          marginBottom: isMobile ? '1.5rem' : '2rem',
          fontSize: isMobile ? '0.9rem' : '1rem'
        }}>
          {error.type === 'NO_RECORDS' ? (
            <div>
              <strong>{t('dns.noRecords.title')}</strong>
              <div style={{ marginTop: '0.5rem' }}>{t('dns.noRecords.message', { domain: error.domain })}</div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', opacity: 0.9 }}>
                {t('dns.noRecords.hint')}
              </div>
            </div>
          ) : (
            t('common.error', { message: error.message })
          )}
        </div>
      )}

      {dnsData && !loading && (
        <RecordTable dnsData={dnsData} rawDnsData={rawDnsData} />
      )}
    </div>
  );
};

export default DnsLookupPage;
